import { useState } from 'react';
import { Trash2, Plus, Eye, FileImage } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Lightbox } from './Lightbox';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function FloorplanManager({ propertyId, floorplans = [], onUpdate }) {
  const [isOpen, setIsOpen] = useState(false);
  const [localPlans, setLocalPlans] = useState(floorplans);
  const [uploading, setUploading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openManager = () => {
    setLocalPlans([...floorplans]);
    setIsOpen(true);
  };

  const getUrl = (path) => `${API}/files/${path}`;

  const handleUpload = async (files) => {
    setUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const fd = new FormData();
        fd.append('file', files[i]);
        const { data } = await axios.post(`${API}/properties/${propertyId}/floorplans`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setLocalPlans((prev) => [...prev, data.path]);
      }
      toast.success(`${files.length} planimetria/e caricata/e`);
      onUpdate();
    } catch {
      toast.error('Errore nel caricamento');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async (path) => {
    try {
      await axios.delete(`${API}/properties/${propertyId}/floorplans?floorplan_path=${encodeURIComponent(path)}`, {
        withCredentials: true,
      });
      setLocalPlans((prev) => prev.filter((p) => p !== path));
      toast.success('Planimetria rimossa');
      onUpdate();
    } catch {
      toast.error('Errore nella rimozione');
    }
  };

  return (
    <>
      <Button variant="ghost" size="sm" onClick={openManager} title="Gestisci planimetrie" data-testid={`manage-floorplans-${propertyId}`}>
        <FileImage size={16} className="text-accent" />
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Gestione Planimetrie</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <label className="flex items-center justify-center border-2 border-dashed border-border rounded-xl p-6 cursor-pointer hover:bg-background-alt transition-colors" data-testid="floorplan-upload-area">
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => e.target.files && handleUpload(e.target.files)}
                disabled={uploading}
              />
              <div className="text-center">
                <Plus size={24} className="mx-auto text-text-muted mb-2" />
                <p className="font-sans text-sm text-text-muted">
                  {uploading ? 'Caricamento...' : 'Clicca per aggiungere planimetrie'}
                </p>
              </div>
            </label>

            {localPlans.length > 0 ? (
              <>
                <p className="font-sans text-sm text-text-muted">{localPlans.length} planimetria/e</p>
                <div className="grid grid-cols-2 gap-3">
                  {localPlans.map((plan, index) => (
                    <div key={plan} className="relative group rounded-lg overflow-hidden border border-border">
                      <div className="aspect-[4/3] relative">
                        <img src={getUrl(plan)} alt={`Planimetria ${index + 1}`} className="w-full h-full object-contain bg-gray-50" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                          <button onClick={() => setLightboxIndex(index)} className="p-2 bg-white/90 rounded-full hover:bg-white"><Eye size={16} /></button>
                          <button onClick={() => handleRemove(plan)} className="p-2 bg-red-500/90 rounded-full hover:bg-red-500 text-white"><Trash2 size={16} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-center text-text-muted font-sans py-6">Nessuna planimetria caricata</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {lightboxIndex !== null && (
        <Lightbox
          images={localPlans.map(getUrl)}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
}
